import React from "react"
import styled from "@emotion/styled"
import { css, type Theme } from "@emotion/react"
import { Link, NavLink } from "react-router"
import {
  type CoreButtonProps,
  InternalButton,
  InternalLink,
  InternalNavLink,
  getButtonIconColor,
  heightStyles,
  variantStyles,
  activeCss,
} from "@myvp/shared/src/components/buttons/button-utils"
import {
  type IconColor,
  type IconElement,
} from "@myvp/shared/src/styles/icon-utils"

interface IconButtonProps extends Omit<CoreButtonProps, "height"> {
  /**
   * this is a render prop
   *
   * you can either pass the icon component function
   * - e.g. `icon={Edit}`
   *
   * or you can pass an inline function that returns your icon
   * - e.g. `icon={() => <Notification />}`
   * - this is useful if you want to ignore the arguments passed to the render prop
   */
  icon: IconElement
  variant: "primary" | "secondary" | "tertiary"
  height: "xsmall" | "small" | "medium" | "large"
  iconColor?: IconColor // Only used for tertiary variant
  useAltHoverColor?: boolean
  hasIndicator?: boolean
}
export interface ExternalIconButtonProps
  extends Omit<React.ComponentProps<"button">, "children">, IconButtonProps {
  internalType?: "button"
}
interface ExternalIconLinkButtonProps
  extends Omit<React.ComponentProps<typeof Link>, "children">, IconButtonProps {
  internalType: "link"
}
interface ExternalIconNavLinkButtonProps
  extends
    React.ComponentProps<typeof NavLink>,
    Omit<IconButtonProps, "children" | "className"> {
  internalType: "navlink"
}

export const baseStyles = () => css`
  position: relative;
  display: inline-flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  border-radius: 4px;
  gap: 8px;
  cursor: pointer;
`
const widthStyles = {
  xsmall: css`
    width: 24px;
    min-width: 24px;
  `,
  small: css`
    width: 32px;
    min-width: 32px;
  `,
  medium: css`
    width: 40px;
    min-width: 40px;
  `,
  large: css`
    width: 44px;
    min-width: 44px;
  `,
}

const IconButton = (
  props:
    | ExternalIconButtonProps
    | ExternalIconLinkButtonProps
    | ExternalIconNavLinkButtonProps
) => {
  const styles = [
    baseStyles(),
    widthStyles[props.height],
    heightStyles[props.height],
    variantStyles[props.variant],
    activeCss(!!props.isAnchorOpen),
    props.useAltHoverColor &&
      ((theme: Theme) => css`
        :hover {
          background-color: ${theme.palette.surface.neutral.selectedhover};
        }
        :active {
          background-color: ${theme.palette.surface.neutral.selectedpress};
        }
      `),
  ]
  const disabled =
    "disabled" in props
      ? props.disabled
      : props["aria-disabled"] === true || props["aria-disabled"] === "true"
  const internalChildren = (
    <>
      {props.icon({
        size: "medium",
        color: getButtonIconColor({
          variant: props.variant,
          disabled,
          iconColor: props.iconColor,
        }),
      })}
      {props.hasIndicator && (
        <div
          data-testid="indicator"
          css={(theme) => css`
            position: absolute;
            border-radius: 50%;
            background-color: ${theme.palette.content.secondary.default};
            top: -5px;
            right: -5px;
            width: 12px;
            height: 12px;
          `}
        />
      )}
    </>
  )

  if (props.internalType === "navlink") {
    const linkProps = omitIconButtonProps(props)
    return (
      <InternalNavLink {...linkProps} css={styles}>
        {internalChildren}
      </InternalNavLink>
    )
  } else if (props.internalType === "link") {
    const linkProps = omitIconButtonProps(props)
    return (
      <InternalLink {...linkProps} css={styles}>
        {internalChildren}
      </InternalLink>
    )
  } else {
    const buttonProps = omitIconButtonProps(props)
    return (
      <InternalButton {...buttonProps} css={styles}>
        {internalChildren}
      </InternalButton>
    )
  }
}

export default styled(IconButton)``

const omitIconButtonProps = <P extends React.ComponentProps<typeof IconButton>>(
  allProps: P
) => {
  const {
    internalType,
    variant,
    height,
    icon,
    iconColor,
    isAnchorOpen,
    useAltHoverColor,
    hasIndicator,
    ...props
  } = allProps
  return props
}
